import React, { useRef, useState, useCallback,useEffect } from "react";
import {
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Radio,
    Button,
    Checkbox,
} from "antd";
import { Avatar, Spin, message, ConfigProvider, theme as antdTheme, Upload } from "antd";
import type { UploadFile } from "antd";
import { useTranslation } from "react-i18next";
import { useLocationData } from "../../hooks/useLocationData";
import { filterOptionVietnamese } from "../../lib/utils";
import { extractApiError } from "../../lib/apiError";
import { CameraOutlined, PlusOutlined } from "@ant-design/icons";
import { mixDelV1, mixGetV1, mixPostV1, mixPostV2 } from "../../services/mixin";
import useAuthStore from "../../store/authStore";
const { Option } = Select;
const { TextArea } = Input;

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit?: (values: any) => void;
}

const CreateCustomerModal: React.FC<Props> = ({
    open,
    onClose,
    onSubmit,
}) => {
    const [form] = Form.useForm();
    const { t, i18n } = useTranslation();
    const { provinceOptions, wardOptions, loadWards, setWardOptions } = useLocationData();

    // "Người tạo" (staff_id): tài khoản không phải super admin -> tự gán chính mình và KHÔNG được đổi.
    // Cấu trúc user: tài khoản nhân viên thì user.info CHÍNH LÀ bản ghi staff (id staff = info.id);
    // tài khoản admin thì info là bản ghi user -> chỉ tin info.staff_id.
    const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const currentUserType = Number(storedUser?.type ?? storedUser?.info?.type ?? 0);
    const currentStaffId = currentUserType === 1
        ? (Number(storedUser?.info?.staff_id ?? 0) || null)
        : (Number(storedUser?.info?.staff_id ?? storedUser?.info?.id ?? 0) || null);

    // Dropdown options
    const [occupations, setOccupations] = useState<{ label: string; value: number }[]>([]);
    const [saleTag, setSaleTag] = useState<{ label: string; value: number; color: string }[]>([]);
    const [careStatuses, setCareStatuses] = useState<{ label: string; value: number; color: string}[]>([]);
    const [staff, setStaff] = useState<{ label: string; value: number }[]>([]);
    const [telesales, setTelesales] = useState<{ label: string; value: number }[]>([]);
    const [channelConnection, setChannelConnection] = useState<{ label: string; value: number }[]>([]);

    // const emptyForm = {
    //     display_name: '',
    //     phone: "",
    //     email:'',
    //     address: "",
    //     gender: null as number | null,
    //     province_code: null as string | null,
    //     ward_code: null as string | null,
    //     birthday: null as string | null,
    //     note: "",
    //     source: '',
    //     customer_tag_id: null as number | null,
    //     customer_care_status_id: null as number | null,
    //     // staff_id: null as number | null,
    //     // telesale_id: null as number | null,
    //     customer_occupation_id: null as number | null,
    // }

    // const [customer, setCustomer] = useState(emptyForm);

    // Fetch dropdown options
    const fetchOptions = useCallback(async () => {
        try {
            const [careRes, occRes, saleTagRes, staffRes, telesaleRes, providerRes] = await Promise.all([
                mixGetV1('/api/v1/be/customer-care-statuses', { limit: 100, sort: ["id,desc"] }).catch(() => null),
                mixGetV1('/api/v1/be/customer-occupations', { take: 100 }).catch(() => null),
                mixGetV1('/api/v1/be/customer-tags', { limit: 100, sort: ["id,desc"] }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 2 }).catch(() => null),
                mixGetV1('/api/v1/be/staffs', { limit: 200, type_department_id: 3 }).catch(() => null),
                mixGetV1('/api/v1/be/oauth-accounts', { limit: 100, sort: ["id,desc"] }).catch(() => null),
            ]);

            if (careRes?.status && careRes.data?.items) {
                setCareStatuses(careRes.data.items.map((s: any) => ({ label: s.name, value: s.id, color: s.color })));
            }
            if (occRes?.status && occRes.data?.items) {
                setOccupations(occRes.data.items.map((o: any) => ({ label: o.name, value: o.id })));
            }
            if (saleTagRes?.status && saleTagRes.data?.items) {
                setSaleTag(saleTagRes.data.items.map((u: any) => ({ label: u.name, value: u.id, color: u.color })));
            }
            if (staffRes?.status && staffRes.data?.items) {
                setStaff(staffRes.data.items.map((u: any) => ({ label: u.name, value: u.id })));
            }
            if (telesaleRes?.status && telesaleRes.data?.items) {
                setTelesales(telesaleRes.data.items.map((u: any) => ({ label: u.name, value: u.id })));
            }
            if (providerRes?.status && providerRes.data?.items) {
                const Channelconnections = providerRes.data.items.flatMap((item: any) =>
                    (item.connections || []).map((conn: any) => ({
                        label: conn.display_name,
                        value: conn.id,
                    }))
                );
                setChannelConnection(Channelconnections)
            }
        } catch (error) {
            console.error("Error fetching options:", error);
        }
    }, []);

    useEffect(() => {
        fetchOptions();
    }, [fetchOptions]);

    // Mở modal: tài khoản không phải super admin -> tự gán "Người tạo" là chính mình
    useEffect(() => {
        if (open && !isSuperAdmin && currentStaffId) {
            form.setFieldsValue({ staff_id: currentStaffId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isSuperAdmin, currentStaffId]);

    // Đảm bảo option của chính mình luôn hiển thị tên (kể cả khi không nằm trong danh sách trực page)
    const creatorOptions = (!isSuperAdmin && currentStaffId && !staff.some((s) => s.value === currentStaffId))
        ? [{ label: storedUser?.info?.name || storedUser?.fullname || `#${currentStaffId}`, value: currentStaffId }, ...staff]
        : staff;

    // Avatar
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [avatarUploading, setAvatarUploading] = useState(false);

    const [avatarFile, setAvatarFile] = useState<File | null>(null);

    // Hình ảnh đính kèm khi tạo khách (param "photos" — array files, tối đa 10MB/ảnh, chọn được nhiều ảnh)
    const [photoList, setPhotoList] = useState<UploadFile[]>([]);
    const beforeAddPhoto = (file: File) => {
        if (!file.type.startsWith("image/")) {
            message.error(t("customer.photo_invalid_type") || "Chỉ chấp nhận tệp hình ảnh");
            return Upload.LIST_IGNORE;
        }
        if (file.size > 10 * 1024 * 1024) {
            message.error(t("customer.photo_too_large", { name: file.name }) || `Ảnh "${file.name}" vượt quá 10MB`);
            return Upload.LIST_IGNORE;
        }
        return false; // giữ file trong danh sách, không tự upload — gửi kèm khi bấm Tạo
    };
    const [preview, setPreview] = useState<string | null>(null);

    // const handleUploadAvatar = async (file: File) => {
    //     try {
    //         // preview demo local image
    //         const previewUrl = URL.createObjectURL(file);

    //         // fake loading
    //         await new Promise((resolve) =>
    //             setTimeout(resolve, 1200)
    //         );

    //         setCustomer((prev) => ({
    //             ...prev,
    //             avatar: previewUrl,
    //         }));

    //         message.success("Cập nhật ảnh đại diện thành công");
    //     } catch (error) {
    //         message.error("Upload thất bại");
    //     }
    // };

    const handleSave = async (values: any): Promise<boolean> => {

        const { channel_connection_id, ...rest } = values;

       // Hình ảnh đính kèm: mixPostV2 tự chuyển mảng thành photos[] trong FormData
       const photoFiles = photoList.map((f) => f.originFileObj).filter(Boolean) as File[];

       const payload = {
            ...rest,
            avatar: avatarFile || null,
            photos: photoFiles.length > 0 ? photoFiles : undefined,
            channel_connection_ids: channel_connection_id
                ? [channel_connection_id]
                : [],
        };

        // console.log(payload)

        const res = await mixPostV2('/api/v1/be/customers', payload);
        
        if (res.status) {
            message.success(t("customer.create_success") || "Tạo khách hàng thành công");
            return true;
        } else {
            // Ưu tiên hiển thị đúng lỗi API trả về (đã map sang tiếng Việt), nếu không có thì dùng thông báo mặc định
            const apiErr = extractApiError(res, i18n.language);
            message.error(apiErr || t("customer.create_error") || "Tạo khách hàng thất bại");
            return false;
        }
    }

    // const xoa = async () => {
    //     const res = await mixDelV1(`/api/v1/be/customers/${52}`, {
    //         method: 'delete'
    //     });
    // };

    // useEffect(() => {
    //     xoa()
    // },[])

    const handleFinish = async (values: any) => {

        if (values.birthday) {
            values.birthday = values.birthday.format('YYYY-MM-DD');
        }

        // API nhận is_foreign dạng số: 0 = trong nước, 1 = nước ngoài
        values.is_foreign = values.is_foreign ? 1 : 0;

        const success = await handleSave(values);

        if (!success) return; // ❗ chặn nếu fail

        onSubmit?.(values);
        form.resetFields();
        setPhotoList([]);
        setWardOptions([]);
        onClose();
    };

    const handleProvinceChange = (value: string) => {
        form.setFieldsValue({ ward: undefined });
        setWardOptions([]);
        if (value) loadWards(value);
    };

    

    // const careerOptions = [
    //     { value: "office", label: t('customer.office_worker') },
    //     { value: "student", label: t('customer.student') },
    //     { value: "business", label: t('customer.business') },
    // ];

    // const careStatusOptions = [
    //     { value: "new", label: t('customer.new_customer') },
    //     { value: "consulting", label: t('customer.consulting') },
    //     { value: "care1", label: t('customer.care_round_1') },
    //     { value: "care2", label: t('customer.care_round_2') },
    //     { value: "purchased", label: t('customer.purchased') },
    // ];

    const responsibleOptions = [
        { value: "huan", label: "PhanCao Huân" },
        { value: "hai", label: "Nguyễn Hải" },
        { value: "anh", label: "Trần Minh Anh" },
    ];

    const titleOptions = [
        { value: "mr", label: "Mr." },
        { value: "ms", label: "Ms." },
        { value: "mrs", label: "Mrs." },
    ];

    return (
        <ConfigProvider
            theme={{
                algorithm: document.documentElement.classList.contains("dark")
                    ? antdTheme.darkAlgorithm
                    : antdTheme.defaultAlgorithm,
                token: {
                    colorPrimary: "#3e91ab", // [#3e91ab]
                },
            }}
        >
            <Modal
                title={
                    <div className="text-[24px] font-semibold text-center t-text-white">
                        {t('customer.create_customer')}
                    </div>
                }
                open={open}
                onCancel={() => {
                    form.resetFields();
                    setPhotoList([]);
                    onClose();
                }}
                footer={null}
                width={900}
                centered
                className="
                    dark:[&_.ant-modal-content]:bg-[#1f1f1f]
                    dark:[&_.ant-modal-header]:bg-[#1f1f1f]
                    dark:[&_.ant-modal-title]:text-white
                    dark:[&_.ant-modal-close]:text-gray-300
                "
            >
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                    <div className="grid grid-cols-3 gap-4">

                        {/* ================= LEFT COLUMN ================= */}
                        <div className="col-span-2">
                            <div className="font-semibold mb-4 t-text-white text-[16px]">
                                {t('customer.customer_details')}
                            </div>

                            <Form.Item
                                label={t('customer.customer_avatar')}
                                name="avatar"
                            >
                                <div
                                    className="relative h-[80px] w-[80px] cursor-pointer group"
                                    onClick={() => fileInputRef.current?.click()}
                                    title="Thay đổi ảnh đại diện"
                                >
                                    <Avatar
                                        size={80}
                                        src={preview || undefined}
                                        className="
                                            h-[80px] w-[80px]
                                            border border-slate-200
                                            dark:border-slate-700
                                        "
                                        onError={() => {
                                            console.warn("Avatar load failed:", preview);
                                            return true;
                                        }}
                                    >
                                        {form.getFieldValue("display_name")
                                            ?.charAt(0)
                                            ?.toUpperCase() || "?"}
                                    </Avatar>

                                    {/* OVERLAY */}
                                    <div
                                        className={`
                                            absolute inset-0
                                            rounded-full
                                            bg-black/45
                                            flex items-center justify-center
                                            transition-all duration-200
                                            ${avatarUploading
                                                ? "opacity-100"
                                                : "opacity-0 group-hover:opacity-100"
                                            }
                                        `}
                                    >
                                        {avatarUploading ? (
                                            <Spin size="small" />
                                        ) : (
                                            <CameraOutlined className="text-[22px] text-white" />
                                        )}
                                    </div>

                                    {/* INPUT */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];

                                            if (file) {

                                                if (file.size > 5 * 1024 * 1024) {
                                                    message.error("Ảnh tối đa 5MB");
                                                    return;
                                                }

                                                setAvatarUploading(true);

                                                try {
                                                    const previewUrl = URL.createObjectURL(file);

                                                    setAvatarFile(file);   // ✅ file thật
                                                    setPreview(previewUrl); // ✅ preview
                                                } finally {
                                                    setAvatarUploading(false);
                                                }
                                            }

                                            e.target.value = "";
                                        }}
                                    />
                                </div>
                            </Form.Item>

                            <div className="grid grid-cols-2 gap-x-4 mt-[30px]">

                                {/* Khách nước ngoài (is_foreign): chỉ đánh dấu nguồn gốc khách,
                                    Tỉnh/TP - Phường/Xã vẫn nhập bình thường vì không bắt buộc */}
                                <Form.Item
                                    name="is_foreign"
                                    valuePropName="checked"
                                    className="col-span-2 !mb-2"
                                >
                                    <Checkbox>
                                        {t('customer.foreign_customer') || "Khách nước ngoài"}
                                    </Checkbox>
                                </Form.Item>

                                <Form.Item
                                    label={t('customer.customer_name')}
                                    name="display_name"
                                    rules={[{ required: true, message: t('customer.required') }]}
                                    className="col-span-1"
                                >
                                    <Input
                                        className="
                                            dark:[&_.ant-input]:bg-[#2a2a2a]
                                            dark:[&_.ant-input]:border-[#303030]
                                            dark:[&_.ant-input]:text-white
                                        "
                                        placeholder={t('customer.customer_name')}
                                    />
                                </Form.Item>

                                {/* Họ tên thật của khách (display_name là tên hiển thị) */}
                                <Form.Item
                                    label={t('customer.customer_fullname') || "Họ tên thật"}
                                    name="fullname"
                                    className="col-span-1"
                                >
                                    <Input
                                        className="
                                            dark:[&_.ant-input]:bg-[#2a2a2a]
                                            dark:[&_.ant-input]:border-[#303030]
                                            dark:[&_.ant-input]:text-white
                                        "
                                        placeholder={t('customer.customer_fullname') || "Họ tên thật"}
                                    />
                                </Form.Item>

                                <Form.Item label={t('customer.province')} name="province_code">
                                    <Select
                                        className="
                                            dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                            dark:[&_.ant-select-selector]:border-[#303030]
                                        "
                                        placeholder={t('customer.select_province')}
                                        options={provinceOptions}
                                        onChange={handleProvinceChange}
                                        showSearch
                                        filterOption={filterOptionVietnamese}
                                        allowClear
                                    />
                                </Form.Item>

                                <Form.Item label={t('customer.phone')} name="phone">
                                    <Input
                                        className="
                                            dark:[&_.ant-input]:bg-[#2a2a2a]
                                            dark:[&_.ant-input]:border-[#303030]
                                            dark:[&_.ant-input]:text-white
                                        "
                                        placeholder={t('customer.phone')}
                                    />
                                </Form.Item>

                                <Form.Item label={t('customer.ward')} name="ward_code">
                                    <Select
                                        className="
                                            dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                            dark:[&_.ant-select-selector]:border-[#303030]
                                        "
                                        placeholder={t('customer.select_ward')}
                                        options={wardOptions}
                                        showSearch
                                        filterOption={filterOptionVietnamese}
                                        allowClear
                                    />
                                </Form.Item>

                                <Form.Item label="Email" name="email">
                                    <Input
                                        className="
                                            dark:[&_.ant-input]:bg-[#2a2a2a]
                                            dark:[&_.ant-input]:border-[#303030]
                                            dark:[&_.ant-input]:text-white
                                        "
                                        placeholder="Email"
                                    />
                                </Form.Item>



                                <Form.Item label={t('customer.dob')} name="birthday">
                                    <DatePicker className="w-full"/>
                                </Form.Item>

                                <Form.Item
                                    label={t('customer.address')}
                                    name="address"
                                >
                                    <Input
                                        className="
                                            dark:[&_.ant-input]:bg-[#2a2a2a]
                                            dark:[&_.ant-input]:border-[#303030]
                                            dark:[&_.ant-input]:text-white
                                        "
                                        placeholder={t('customer.address_placeholder')}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label={t('customer.source')}
                                    name="channel_connection_id"
                                >
                                    <Select
                                        placeholder={t('customer.select_source')}
                                        className="w-full"
                                        allowClear
                                        options={channelConnection}
                                    />
                                </Form.Item>

                                <Form.Item label={t('customer.gender')} name="gender" className="col-span-2">
                                    <Radio.Group
                                        className="
                                            [&_.ant-radio-checked_.ant-radio-inner]:border-[#3e91ab]
                                            [&_.ant-radio-checked_.ant-radio-inner]:bg-[#3e91ab]
                                            [&_.ant-radio:hover_.ant-radio-inner]:border-[#3e91ab]
                                        "
                                    >
                                        <Radio value={1}>{t('customer.male')}</Radio>
                                        <Radio value={2}>{t('customer.female')}</Radio>
                                        <Radio value={0}>{t('customer.other_gender')}</Radio>
                                    </Radio.Group>
                                </Form.Item>

                                <Form.Item label={t('customer.career')} name="customer_occupation_id">
                                    <Select
                                        className="
                                            dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                            dark:[&_.ant-select-selector]:border-[#303030]
                                        "
                                        placeholder={t('customer.select_career')}
                                        options={occupations}
                                    />
                                </Form.Item>

                                {/* <Form.Item label="Sale tags" name="customer_tag_id">
                                    <Select
                                        mode="multiple"
                                        placeholder={t('customer.select_sale_tags')}
                                        optionLabelProp="label"
                                        className="
                                            [&_.ant-select-selector]:rounded-lg
                                            dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                            dark:[&_.ant-select-selector]:border-[#303030]
                                        "
                                        options={
                                            saleTag.length > 0
                                                ? saleTag.map(item => ({
                                                    value: item.value,
                                                    label: (
                                                        <span 
                                                            className="bg-pink-500 text-white px-2 py-1 rounded-md text-xs"
                                                            style={{ backgroundColor: item.color }}
                                                        >
                                                            {item.label}
                                                        </span>
                                                    ),
                                                }))
                                                : []
                                        }
                                        dropdownRender={(menu) => (
                                            <div className="dark:bg-[#1f1f1f] p-2 rounded-lg">
                                                {menu}
                                            </div>
                                        )}
                                    />
                                </Form.Item> */}

                                <Form.Item label={t('customer.care_status')} name="customer_care_status_id">
                                    <Select
                                        placeholder={t('customer.select_care_status')}
                                        optionLabelProp="label"
                                        popupClassName="care-status-dropdown"
                                        className="
                                            [&_.ant-select-selector]:rounded-lg
                                            dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                            dark:[&_.ant-select-selector]:border-[#303030]
                                        "
                                        options={
                                            careStatuses.length > 0
                                                ? careStatuses.map(item => ({
                                                    value: item.value,
                                                    label: (
                                                        <span 
                                                            className="bg-pink-500 text-white px-2 py-1 rounded-md text-xs"
                                                            style={{ backgroundColor: item.color }}
                                                        >
                                                            {item.label}
                                                        </span>
                                                    ),
                                                }))
                                                : []
                                        }
                                    />
                                </Form.Item>

                                {/* Người tạo: chỉ super admin được đổi; nhân viên (vd trực page) tự gán chính mình */}
                                <Form.Item
                                    label={t('customer.creator')}
                                    name="staff_id"
                                    rules={[{ required: true, message: t('customer.creator_required') }]}
                                >
                                    <Select
                                        className="
                                            dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                            dark:[&_.ant-select-selector]:border-[#303030]
                                        "
                                        placeholder={t('customer.select_creator')}
                                        options={creatorOptions}
                                        disabled={!isSuperAdmin}
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                </Form.Item>

                                {/* Phân công Telesale — gửi telesale_id (alias contact_staff_id) theo tài liệu API tạo khách hàng */}
                                <Form.Item
                                    label={t('customer.assign_telesale')}
                                    name="telesale_id"
                                    rules={[{ required: true, message: t('customer.telesale_required') }]}
                                >
                                    <Select
                                        className="
                                            dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                            dark:[&_.ant-select-selector]:border-[#303030]
                                        "
                                        placeholder={t('customer.select_telesale')}
                                        options={telesales}
                                        showSearch
                                        optionFilterProp="label"
                                        allowClear
                                    />
                                </Form.Item>

                                <Form.Item
                                    label={t('customer.notes')}
                                    name="note"
                                    className="col-span-2"
                                >
                                    <TextArea rows={3} placeholder={t('customer.enter_notes')}/>
                                </Form.Item>

                                {/* Hình ảnh đính kèm (photos[]) — chọn nhiều ảnh cùng lúc, tối đa 10MB/ảnh */}
                                <Form.Item
                                    label={`${t('customer.photos')} (${t('customer.photos_hint')})`}
                                    className="col-span-2"
                                >
                                    <Upload
                                        listType="picture-card"
                                        multiple
                                        accept="image/*"
                                        fileList={photoList}
                                        beforeUpload={beforeAddPhoto}
                                        onChange={({ fileList }) => setPhotoList(fileList)}
                                    >
                                        <div>
                                            <PlusOutlined />
                                            <div className="mt-1 text-xs">{t('customer.add_photo')}</div>
                                        </div>
                                    </Upload>
                                </Form.Item>

                            </div>
                        </div>

                        {/* ================= RIGHT COLUMN ================= */}
                        <div className="col-span-1 border-l pl-4 dark:border-[#303030]">
                            <div className="font-semibold mb-4 t-text-white text-[16px]">
                                {t('customer.contact_info')}
                            </div>

                            <Form.Item label={t('customer.full_name')} name="contactName">
                                <Input
                                    className="
                                        dark:[&_.ant-input]:bg-[#2a2a2a]
                                        dark:[&_.ant-input]:border-[#303030]
                                        dark:[&_.ant-input]:text-white
                                    "
                                    placeholder={t('customer.enter_full_name')}
                                />
                            </Form.Item>

                            <Form.Item label={t('customer.title_position')} name="contactTitle">
                                <Select
                                    className="
                                        dark:[&_.ant-select-selector]:bg-[#2a2a2a]
                                        dark:[&_.ant-select-selector]:border-[#303030]
                                    "
                                    placeholder={t('customer.select_title')}
                                    options={titleOptions}
                                />
                            </Form.Item>

                            <Form.Item label={t('customer.position')} name="contactPosition">
                                <Input
                                    className="
                                        dark:[&_.ant-input]:bg-[#2a2a2a]
                                        dark:[&_.ant-input]:border-[#303030]
                                        dark:[&_.ant-input]:text-white
                                    "
                                    placeholder={t('customer.enter_position')}
                                />
                            </Form.Item>

                            <Form.Item label={t('customer.phone')} name="contactPhone">
                                <Input
                                    className="
                                        dark:[&_.ant-input]:bg-[#2a2a2a]
                                        dark:[&_.ant-input]:border-[#303030]
                                        dark:[&_.ant-input]:text-white
                                    "
                                    placeholder={t('customer.enter_phone')}
                                />
                            </Form.Item>

                            <Form.Item label={t('customer.email')} name="contactEmail">
                                <Input
                                    className="
                                        dark:[&_.ant-input]:bg-[#2a2a2a]
                                        dark:[&_.ant-input]:border-[#303030]
                                        dark:[&_.ant-input]:text-white
                                    "
                                    placeholder={t('customer.enter_email')}
                                />
                            </Form.Item>

                            <Form.Item label={t('customer.gender')} name="contactGender">
                                <Radio.Group
                                    className="
                                        [&_.ant-radio-checked_.ant-radio-inner]:border-[#3e91ab]
                                        [&_.ant-radio-checked_.ant-radio-inner]:bg-[#3e91ab]
                                        [&_.ant-radio:hover_.ant-radio-inner]:border-[#3e91ab]
                                    "
                                >
                                    <Radio value="male">{t('customer.male')}</Radio>
                                    <Radio value="female">{t('customer.female')}</Radio>
                                    <Radio value="other">{t('customer.other_gender')}</Radio>
                                </Radio.Group>
                            </Form.Item>
                        </div>
                    </div>

                    {/* ================= FOOTER ================= */}
                    <div className="flex justify-end gap-3 mt-8">
                        <Button
                            onClick={() => {
                                form.resetFields();
                                onClose();
                            }}
                            className="dark:bg-[#2a2a2a] dark:border-[#303030] text-000000e0"
                        >
                            {t('customer.cancel')}
                        </Button>

                        <Button
                            htmlType="submit"
                            type="primary"
                            className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                        >
                            {t('customer.create')}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default CreateCustomerModal;